import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import { useSearchParams } from '../../../hooks/custom';

/**
 * TabNavigation component for tab-based navigation within modules
 */
const TabNavigation = ({
  tabs,
  value,
  onChange,
  paramName = 'tab',
  baseUrl = '',
  variant = 'standard',
  centered = false,
  scrollable = false,
  fullWidth = false,
  showBorder = true,
  sx = {}
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { getParam, setParam } = useSearchParams();
  
  // If value is not provided, get it from URL params
  const currentValue = value !== undefined 
    ? value 
    : (getParam(paramName) || (tabs.length > 0 ? tabs[0].value : ''));
  
  // Handle tab change
  const handleChange = (event, newValue) => {
    if (onChange) {
      onChange(event, newValue);
    } else {
      // If no onChange handler is provided, update URL param and navigate if baseUrl is provided
      setParam(paramName, newValue);
      
      if (baseUrl) {
        const targetTab = tabs.find(tab => tab.value === newValue);
        if (targetTab && targetTab.path) {
          navigate(`${baseUrl}/${targetTab.path}`);
        }
      }
    }
  };
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        mb: 3,
        borderRadius: 1,
        overflow: 'hidden',
        border: showBorder ? `1px solid ${alpha(theme.palette.divider, 0.7)}` : 'none',
        ...sx
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={currentValue}
          onChange={handleChange}
          variant={isMobile ? 'scrollable' : (scrollable ? 'scrollable' : (fullWidth ? 'fullWidth' : 'standard'))}
          scrollButtons={isMobile || scrollable}
          allowScrollButtonsMobile={isMobile || scrollable}
          centered={!isMobile && !scrollable && centered}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 'medium',
              fontSize: '0.95rem',
              minHeight: 48,
              transition: 'all 0.2s',
              '&.Mui-selected': {
                fontWeight: 'bold'
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05)
              }
            }
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              icon={tab.icon}
              iconPosition={tab.iconPosition || 'start'}
              disabled={tab.disabled}
              sx={tab.sx}
            />
          ))}
        </Tabs>
      </Box>
    </Paper>
  );
};

TabNavigation.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      path: PropTypes.string,
      icon: PropTypes.node,
      iconPosition: PropTypes.oneOf(['start', 'end', 'top', 'bottom']),
      disabled: PropTypes.bool,
      sx: PropTypes.object
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  paramName: PropTypes.string,
  baseUrl: PropTypes.string,
  variant: PropTypes.oneOf(['standard', 'scrollable', 'fullWidth']),
  centered: PropTypes.bool,
  scrollable: PropTypes.bool,
  fullWidth: PropTypes.bool,
  showBorder: PropTypes.bool,
  sx: PropTypes.object
};

export default TabNavigation;
